import React, { useState } from 'react'
import { apiPut } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import { useForm } from 'react-hook-form'
import useToastContext from 'hooks/useToastContext'
import OInputField from 'components/reusable/OInputField'
import { useTranslation } from 'react-i18next'
import { numericInputPattern, preventMaxInput } from 'utils/validations'
import ErrorMessage from 'components/ErrorMessage'

const GenerateSalaryEdit = ({ allGenerateSalary, currentItem, handleEdit }) => {
  const { t } = useTranslation()
  const notification = useToastContext()
  const [isLoading, setIsLoading] = useState(false)
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      otherDeduction: currentItem?.otherDeduction || '',
      otherAllowance: currentItem?.otherAllowance || ''
    }
  })

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      const payload = {
        otherAllowance: Number(data.otherAllowance) || 0,
        otherDeduction: Number(data.otherDeduction) || 0
      }
      
      const path = `${apiPath.updateGenerateSalary}/${currentItem._id}`
      const result = await apiPut(path, payload)
      
      if (result.data.success) {
        allGenerateSalary()
        handleEdit(false)
        notification.success(result?.data.message)
      } else {
        notification.error(result?.data.message)
      }
    } catch (error) {
      console.error('Error updating salary:', error)
      notification.error(error.message || 'Failed to update salary')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none'>
      <div className='relative w-auto max-w-3xl mx-auto my-6'>
        <div className='relative flex flex-col w-full bg-white rounded-lg shadow-lg outline-none focus:outline-none min-w-[762px]'>
          {/* Modal Header */}
          <div className='flex items-center justify-between p-5 border-b border-solid rounded-t border-slate-200'>
            <h3 className='text-xl font-semibold'>{t('EDIT_GENERATE_SALARY')}</h3>
            <button
              className='flex items-center justify-center w-8 h-8 ml-auto text-3xl leading-none text-black bg-transparent border-2 border-transparent rounded-full focus:outline-none hover:border-gray-200'
              onClick={() => handleEdit(false)}
              aria-label='Close'
            >
              <span className='text-4xl text-[#B8BBBF]'>×</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className='relative p-6'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
                <div className='px-2 py-4 md:py-4 sm:px-2 sm:py-8'>
                  <OInputField
                    wrapperClassName='relative z-0 w-full group'
                    inputLabel={
                      <>
                        {t('OTHER_ALLOWANCE')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    type='text'
                    name='otherAllowance'
                    id='otherAllowance'
                    errors={errors}
                    onInput={(e) => preventMaxInput(e, 10)}
                    maxLength={10}
                    onKeyDown={numericInputPattern}
                    register={register('otherAllowance', {
                      required: {
                        value: true,
                        message: t('OTHER_ALLOWANCE_REQUIRED')
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('ONLY_DIGITS_ALLOWED')
                      },
                      min: {
                        value: 1,
                        message: t('MINIMUM_VALUE_1')
                      },
                      maxLength: {
                        value: 10,
                        message: t('MAX_LENGTH_10')
                      }
                    })}
                  />
                  <ErrorMessage message={errors?.otherAllowance?.message} />
                </div>

                <div className='px-2 py-4 md:py-4 sm:px-2 sm:py-8'>
                  <OInputField
                    wrapperClassName='relative z-0 w-full group'
                    inputLabel={
                      <>
                        {t('OTHER_DEDUCTION')}
                        <span className='text-red-500'>*</span>
                      </>
                    }
                    type='text'
                    name='otherDeduction'
                    id='otherDeduction'
                    errors={errors}
                    onInput={(e) => preventMaxInput(e, 10)}
                    maxLength={10}
                    onKeyDown={numericInputPattern}
                    register={register('otherDeduction', {
                      required: {
                        value: true,
                        message: t('OTHER_DEDUCTION_REQUIRED')
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('ONLY_DIGITS_ALLOWED')
                      },
                      min: {
                        value: 1,
                        message: t('MINIMUM_VALUE_1')
                      },
                      maxLength: {
                        value: 10,
                        message: t('MAX_LENGTH_10')
                      }
                    })}
                  />
                  <ErrorMessage message={errors?.otherDeduction?.message} />
                </div>
              </div>

              {/* Modal Footer */}
              <div className='flex items-center justify-center p-6 border-t border-solid rounded-b border-slate-200'>
                <button
                  type='button'
                  className='px-12 py-2.5 text-sm font-normal text-black transition-all duration-150 ease-linear bg-[#E1E1E1] rounded outline-none focus:outline-none mr-6 hover:bg-gray-200'
                  onClick={() => handleEdit(false)}
                  disabled={isLoading}
                >
                  {t('O_BACK')}
                </button>
                <button
                  type='submit'
                  className='px-8 py-2.5 text-sm font-normal text-white transition-all duration-150 ease-linear rounded shadow outline-none bg-LightBlue focus:outline-none hover:shadow-lg disabled:opacity-70'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className='flex items-center justify-center'>
                      <span className='mr-2'>{t('SAVING')}</span>
                      <div className='w-4 h-4 border-2 border-white border-t-2 border-t-transparent rounded-full animate-spin'></div>
                    </div>
                  ) : (
                    t('O_SAVE')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className='' />
    </div>
  )
}
export default GenerateSalaryEdit
